"use client";

import { useEffect, useState } from "react";

type ViewportState = {
  isDesktop: boolean;
  isMobile: boolean;
  isTablet: boolean;
  width: number;
  height: number;
  isHydrated: boolean;
};

/**
 * SSR-safe viewport detection hook that prevents hydration mismatches.
 * Always starts with mobile-first approach during SSR and updates after hydration.
 */
export function useSSRSafeViewport(): ViewportState {
  // Start with mobile-first defaults for SSR consistency
  const [viewport, setViewport] = useState<ViewportState>({
    isDesktop: false,
    isMobile: true,
    isTablet: false,
    width: 375, // Common mobile width
    height: 667, // Common mobile height
    isHydrated: false,
  });

  useEffect(() => {
    const updateViewport = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      setViewport({
        isDesktop: width >= 1024,
        isTablet: width >= 768 && width < 1024,
        isMobile: width < 768,
        width,
        height,
        isHydrated: true,
      });
    };

    // Update immediately after mount
    updateViewport();

    // Listen for resize events
    window.addEventListener("resize", updateViewport);

    return () => {
      window.removeEventListener("resize", updateViewport);
    };
  }, []);

  return viewport;
}

/**
 * SSR-safe reduced motion detection hook.
 * Always returns false during SSR to prevent hydration mismatches.
 */
export function useSSRSafeReducedMotion(): {
  prefersReducedMotion: boolean;
  isHydrated: boolean;
} {
  const [state, setState] = useState({
    prefersReducedMotion: false, // Default to false for SSR
    isHydrated: false,
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const updatePreference = () => {
      setState({
        prefersReducedMotion: mediaQuery.matches,
        isHydrated: true,
      });
    };

    // Update immediately after mount
    updatePreference();

    // Listen for changes
    mediaQuery.addEventListener("change", updatePreference);

    return () => {
      mediaQuery.removeEventListener("change", updatePreference);
    };
  }, []);

  return state;
}
